import { DisplayFile, Uploader, useModal, useUploader } from '@europrocurement/flexy-components';
import { FormStructure } from '@europrocurement/flexy-form';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { ModalMailAttachmentsType } from '@europrocurement/flexy-components/types/GenericModalMailType';
import { GenericModalMessageTypes } from '@europrocurement/flexy-components/types/GenericModalMessageTypes';

/**
 * Generate FormStructure based on GenericModalMailContent props
 * Props define defaults values of inputs or/and boolean to display inputs
 *
 * @returns FormStructure[]
 */
const useFormStructureGenericModal = ({
    body,
    attachments,
    bodyinputSx,
    submittingForm = false,
}: GenericModalMessageTypes) => {
    const formStructure = useRef<Array<FormStructure>>([]);
    const { palette } = useTheme();
    const { modalActions } = useModal();
    const { addFiles } = useUploader();

    useEffect(() => {
        if (attachments && attachments.length > 0) {
            const filesArray: File[] = [];
            attachments.forEach((attachment) => filesArray.push(attachment.file));
            addFiles(filesArray);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attachments]);

    // Render default attchment Chip or custom render defined in ModalMailAttachmentsType
    const renderAttachmentFile = (attachment: ModalMailAttachmentsType) => {
        if (attachment.render) {
            return attachment.render;
        }
        return (
            <DisplayFile
                key={`${attachment.file.name}_${attachment.file.type}`}
                nom={attachment.file.name}
                size={attachment.file.size}
                mimetype={attachment.file.type}
                mode="card"
            />
        );
    };

    useEffect(() => {
        // message body
        formStructure.current = [
            {
                type: 'textarea',
                name: 'messageBody',
                inputlabel: 'Message à envoyer',
                sx: {
                    minHeight: '100px',
                    padding: '8px 20px',
                    border: `1px solid ${palette.grey['300']}`,
                    borderRadius: '12px',
                    ':hover': {
                        border: `1px solid ${palette.grey['900']}`,
                    },
                    ':focus-within': {
                        border: `1px solid ${palette.primary.main}`,
                    },
                    transition: 'all ease-in-out 300ms',
                    '&::before': { display: 'none' },
                    '&::after': { display: 'none' },
                    ...bodyinputSx,
                },
            },
        ];
        // Attachments

        formStructure.current = [
            ...formStructure.current,
            {
                type: 'customItem',
                name: 'attachmentsUploader',
                renderField: () => (
                    <Box sx={{ display: 'grid', gap: '4px' }}>
                        <Typography variant="l2dlabel">Pièces-jointes</Typography>
                        <Uploader
                            label="Pièce(s) jointe(s)"
                            sx={{ margin: 0 }}
                            fileUlpoadOverides={{ height: '160px' }}
                        />
                    </Box>
                ),
            },
        ];
        if (attachments && attachments.length > 0) {
            // Display current attachments in the context
            formStructure.current = [
                ...formStructure.current,
                {
                    type: 'customItem',
                    name: 'attachmentsItems',
                    renderField: () => (
                        <Box>
                            {attachments.map((attachment) => renderAttachmentFile(attachment))}
                        </Box>
                    ),
                },
            ];
        }
        formStructure.current = [
            ...formStructure.current,
            {
                type: 'customItem',
                name: 'buttonsFooter',
                renderField: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            color="primary"
                            sx={{
                                marginTop: '2rem',
                                marginBottom: '16px',
                                color: 'text.gris70',
                                padding: '16px 24px',
                                fontSize: '16px',
                            }}
                            onClick={() => modalActions.close()}
                        >
                            Annuler
                        </Button>
                        {submittingForm ? (
                            <Button
                                variant="contained"
                                type="button"
                                sx={{
                                    marginTop: '2rem',
                                    marginBottom: '16px',
                                    padding: '16px 24px',
                                    fontSize: '16px',
                                }}
                            >
                                <CircularProgress
                                    color="inherit"
                                    size="16px"
                                />
                            </Button>
                        ) : (
                            <Box sx={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    name="sendmessageonly"
                                    disabled={submittingForm}
                                    sx={{
                                        marginTop: '2rem',
                                        marginBottom: '16px',
                                        padding: '16px 24px',
                                        fontSize: '16px',
                                    }}
                                >
                                    Envoyer
                                </Button>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    name="sendmessageandnotification"
                                    disabled={submittingForm}
                                    sx={{
                                        marginTop: '2rem',
                                        marginBottom: '16px',
                                        padding: '16px 24px',
                                        fontSize: '16px',
                                    }}
                                >
                                    Envoyer message et notification
                                </Button>
                            </Box>
                        )}
                    </Box>
                ),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [body]);

    const getFormStructure = () => formStructure.current;

    return {
        getFormStructure,
    };
};

export default useFormStructureGenericModal;
